/**
 * ToolbarButton Component
 * 
 * Reusable button component for the editor toolbar.
 */

import React from 'react';
import { Button } from '../ui/button';
import { NodeSelection } from 'prosemirror-state';

/**
 * ToolbarButton component for editor actions
 * 
 * Args:
 *   onClick (function): Click handler that receives selection info
 *   active (boolean): Whether the button is in active state
 *   disabled (boolean): Whether the button is disabled
 *   icon (component): Lucide icon component to render
 *   label (string): Accessible label for the button
 *   editor (object): TipTap editor instance
 *   savedSelectionRef (ref): Reference to saved selection state
 * 
 * Returns:
 *   JSX.Element: The toolbar button
 */
const ToolbarButton = ({ onClick, active, disabled, icon: Icon, label, title, editor, savedSelectionRef }) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Get current selection BEFORE any state changes
    let selection = editor.state.selection;
    let { from, to } = selection;
    let isNodeSelection = selection instanceof NodeSelection;
    let nodeType = isNodeSelection ? selection.node?.type?.name : null;
    const storedRange = savedSelectionRef.current;

    // Reusar el último rango guardado SOLO cuando el editor perdió el foco (p. ej.
    // un dropdown Radix devolvió el foco a su trigger y ProseMirror colapsó la
    // selección). Si el editor SÍ tiene el foco, su selección real manda —aunque
    // sea un cursor colapsado— para no aplicar sobre un rango obsoleto: antes,
    // poner el cursor en otra línea y crear una lista la aplicaba al texto de la
    // selección anterior. `editor.isFocused` distingue ambos casos.
    if (from === to && !editor.isFocused && storedRange && storedRange.from !== storedRange.to) {
      ({ from, to } = storedRange);
      isNodeSelection = storedRange.isNodeSelection || false;
      nodeType = storedRange.nodeType || null;
    }
    savedSelectionRef.current = { from, to, isNodeSelection, nodeType };

    // Call onClick with the selection
    onClick({ from, to, isNodeSelection, nodeType });
  };

  return (
    <Button
      data-testid={`editor-${label.toLowerCase().replace(/\s/g, '-')}`}
      type="button"
      onMouseDown={handleClick}
      disabled={disabled}
      variant="ghost"
      size="sm"
      className={`hover:bg-sky-100 dark:hover:bg-sky-900/50 transition-colors ${
        active ? 'bg-sky-200 text-sky-700 dark:bg-sky-800 dark:text-sky-200' : ''
      }`}
      title={title || label}
    >
      <Icon className="w-4 h-4" />
    </Button>
  );
};

export default ToolbarButton;

